import { CONSENT_PUBLISH_VALIDATION_MESSAGES, TEMPLATE_CUSTOM_FIELD_MESSAGES, isChoiceElementType, validateConsentDocumentForPublish, } from 'libs.consent.common';
export { SOURCE_FIELD_MAX_LENGTH, CONSENT_PUBLISH_VALIDATION_MESSAGES, collectConsentPublishValidationErrors, isPtsOnlyConsentDocument, validateConsentDocumentForPublish, } from 'libs.consent.common';
/** Scrolls the overlay named by a publish error into view. No-op when the item is not on screen. */
export const revealPublishValidationTarget = (error) => {
    if (!error.overlayItemId) {
        return;
    }
    document
        .querySelector(`[data-testid="${error.overlayItemId}"]`)
        ?.scrollIntoView({ block: 'center', inline: 'nearest' });
};
/** Footer disablement for copied placements — not the other publish validators, which still toast-and-focus. */
export const isCarryForwardBlockingPublish = (document) => {
    const { carryForward } = document;
    if (carryForward && !carryForward.failed && carryForward.placementsConfirmed === false) {
        return true;
    }
    const totalPages = document.totalPages ?? 0;
    for (const element of document.elements) {
        if (element.placed === false || element.needsDimensionConfirmation) {
            return true;
        }
        if (carryForward && totalPages > 0 && (element.page < 1 || element.page > totalPages)) {
            return true;
        }
        if (isChoiceElementType(element.type) &&
            (element.config.ec__options ?? []).some((option) => option.placed === false)) {
            return true;
        }
    }
    return false;
};
/** Rule messages Studio also knows: their English source doubles as the translation key. */
const SHARED_RULE_MESSAGES = new Set([
    ...Object.values(CONSENT_PUBLISH_VALIDATION_MESSAGES),
    ...Object.values(TEMPLATE_CUSTOM_FIELD_MESSAGES),
]);
/** What the author sees for stored data the publish rules cannot read, whether Studio or the bundler finds it. */
export const TEMPLATE_DATA_DAMAGED_MESSAGE = "This template's saved data is damaged, so publishing it again won't help. Contact support.";
/**
 * {@link validateConsentDocumentForPublish} for a document loaded from Library. Unreadable stored roles, or
 * any other shape that makes the shared rules throw, is the bundler's `templateDataInvalid`: reported as
 * {@link TEMPLATE_DATA_DAMAGED_MESSAGE} instead of an uncaught error.
 */
export const validateStoredConsentDocumentForPublish = (document) => {
    if (document.storedDataInvalid) {
        return { message: TEMPLATE_DATA_DAMAGED_MESSAGE };
    }
    try {
        return validateConsentDocumentForPublish(document);
    }
    catch {
        return { message: TEMPLATE_DATA_DAMAGED_MESSAGE };
    }
};
/** Rules only the publish service checks; their own messages name internal fields, so authors get these. */
const SERVER_RULE_MESSAGES = {
    pdfMissing: "This template's source PDF can't be found. Upload the PDF again, then publish.",
    languageUnsupported: "This template's language can't be published yet. Choose a different language, then publish.",
    templateDataInvalid: TEMPLATE_DATA_DAMAGED_MESSAGE,
};
const PUBLISH_REFUSED_MESSAGE = "This template can't be published. Try again, or contact support if it keeps happening.";
/**
 * What to tell the author when a publish request fails. A rule refusal names its first rule; any other 400
 * from the publish service shows that service's message, because the bundle post opts out of the shared 400
 * toast. Everything else, including a 400 from a request that kept that toast, stays "Unknown error".
 */
export const publishFailureSubtitle = (error, translate) => {
    const { config, response } = (error ?? {});
    const metadata = response?.data?.metadata;
    if (metadata?.code === 'consent.validation' && Array.isArray(metadata.errors) && metadata.errors.length > 0) {
        const first = metadata.errors[0];
        if (typeof first.message === 'string' && SHARED_RULE_MESSAGES.has(first.message)) {
            return translate(first.message, first.params);
        }
        return translate(SERVER_RULE_MESSAGES[String(first.rule)] ?? PUBLISH_REFUSED_MESSAGE);
    }
    if (config?.no400Handling && response?.status === 400 && typeof response.data?.message === 'string') {
        return response.data.message;
    }
    return translate('Unknown error');
};
